import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'

import type { Mobility } from '../../api/transport'
import { Icon } from '../../components/Icon'
import { PageBand, PageShell } from '../../components/PageShell'
import { amtcDialNumber, amtcDisplayNumber } from '../../lib/amtc'
import { EMERGENCY_NUMBER } from '../../lib/emergencyNumber'
import { useFocusOnMount } from './focus'
import { Gate } from './Gate'
import { SERVICE_NAME, serviceWhy } from './services'
import { TransportHeader } from './TransportHeader'

/**
 * The M34 holding page (M34-T03), shown while booking is switched off: the
 * safety check, then a planned trip ends at the transport desk's phone number,
 * which is the AMTC number for now (owner ruling Q19). Its stop is counted
 * nowhere (M39-Q1).
 */

/** The approved eligibility matrix (spec §C.3.2): who is recommended what. */
const recommended = (mobility: Mobility) => {
  const service = mobility === 'walks_unaided' ? 'standard_ride' : 'pts'
  return { name: SERVICE_NAME[service], why: serviceWhy(service, mobility) }
}

export function HoldingPage({
  focusHeading,
  onStop,
}: {
  focusHeading: boolean
  onStop: () => void
}): ReactNode {
  const [planned, setPlanned] = useState(false)
  const [mobility, setMobility] = useState<Mobility | null>(null)
  const [handover, setHandover] = useState(false)
  // Focus the gate's heading when the person returns to it (restart, or Back
  // from the handover), but not on first arrival.
  const [returned, setReturned] = useState(focusHeading)

  if (handover && mobility) {
    return (
      <Handover
        mobility={mobility}
        onStop={onStop}
        onBack={() => {
          setReturned(true)
          setHandover(false)
        }}
      />
    )
  }

  return (
    <Gate
      focusHeading={returned}
      // "Pre-paid" returns with online payment, in the booking flow. Until
      // then nothing here is paid, and saying so would be false.
      subtitle="Scheduled rides to our network hospitals."
      planned={planned}
      onPlanned={setPlanned}
      mobility={mobility}
      onMobility={setMobility}
      notice={
        planned &&
        mobility && (
          <>
            We&apos;ll recommend{' '}
            <span className="font-semibold text-ink-1">
              {recommended(mobility).name}
            </span>{' '}
            — {recommended(mobility).why}.
          </>
        )
      }
      canContinue={planned && mobility !== null}
      onStop={onStop}
      onContinue={() => setHandover(true)}
    />
  )
}

/** Until online booking is switched on, a planned trip is booked by phone. */
function Handover({
  mobility,
  onStop,
  onBack,
}: {
  mobility: Mobility
  onStop: () => void
  onBack: () => void
}): ReactNode {
  const navigate = useNavigate()
  const heading = useFocusOnMount<HTMLHeadingElement>()
  const desk = amtcDialNumber()
  const deskShown = amtcDisplayNumber()
  const service = recommended(mobility).name

  return (
    <PageShell measure={640} as="main">
      <TransportHeader onBack={onBack} />
      <PageBand
        className="flex flex-1 flex-col"
        contentClassName="flex flex-1 flex-col gap-4 px-4 py-6"
      >
        <div>
          <h1
            ref={heading}
            tabIndex={-1}
            className="font-display text-d-xs font-medium tracking-display text-ink-1 focus:outline-none"
          >
            Online booking opens soon
          </h1>
          <p className="mt-2 text-[13px] leading-relaxed text-ink-2">
            {desk
              ? `To book ${service} now, call the transport desk.`
              : `We can't take ${service} bookings yet. Please try again later.`}
          </p>
        </div>

        {desk && (
          <div className="rounded-r3 border border-line-1 bg-bg-soft p-3">
            <p className="text-[13px] font-medium text-ink-1">Transport desk</p>
            {deskShown !== null && (
              <p className="mt-0.5 text-[12px] tabular-nums text-ink-3">
                {deskShown}
              </p>
            )}
            <a
              href={`tel:${desk}`}
              className="mt-2.5 inline-flex items-center gap-1.5 rounded-r3 bg-accent px-3 py-2 text-[13px] font-semibold text-accent-on focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              <Icon name="phone" size={14} /> Call the transport desk
            </a>
          </div>
        )}

        {/* Calling 112 from here ends the booking too: someone who needed 112
            should not be able to press Back and carry on booking. */}
        <p className="rounded-r3 border-l-[3px] border-sos bg-sos-bg px-3 py-2.5 text-[12px] leading-relaxed text-ink-1">
          Medical emergency? Call{' '}
          <a
            href={`tel:${EMERGENCY_NUMBER}`}
            onClick={onStop}
            onAuxClick={onStop}
            className="font-semibold text-sos underline underline-offset-2"
          >
            {EMERGENCY_NUMBER}
          </a>{' '}
          — do not wait for this vehicle.
        </p>

        <button
          type="button"
          onClick={() => void navigate('/')}
          className="rounded-r3 border border-line-2 px-3 py-2 text-[13px] font-medium text-ink-1 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          Back to Medcor home
        </button>
      </PageBand>
    </PageShell>
  )
}
